import {
  Box,
  Input,
  Radio,
  RadioGroup,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ProviderSearchResult,
  ProviderSearchSelect,
} from '../../../components/provider-search/ProviderSearchSelect';
import { CustomFieldValue } from '../../../types';
interface CrossCoverageAllApptsSelectProps {
  field: CustomFieldValue | undefined;
  onChange: (field: CustomFieldValue) => void;
  fieldId: string;
  rowId?: string;
}

export const CrossCoverageAllApptsSelect: FC<
  CrossCoverageAllApptsSelectProps
> = ({ field, onChange, fieldId, rowId }) => {
  if (!onChange) {
    console.warn(
      'CrossCoverageAllApptsSelect: onChange prop is required'
    );
  }

  if (!fieldId) {
    console.warn(
      'CrossCoverageAllApptsSelect: fieldId prop is required for proper database operations'
    );
  }

  const [selectedProviders, setSelectedProviders] = useState<
    ProviderSearchResult[]
  >([]);

  const currentValue = field?.value || '';
  const currentProviders = useMemo(
    () =>
      (field?.metadata?.providers as {
        providerId: string;
        providerLabel: string;
      }[]) || [],
    [field?.metadata?.providers]
  );
  const currentWho =
    (field?.metadata?.whoProvidesCoverage as string) || '';
  const currentWhat =
    (field?.metadata?.whatAppointments as string) || '';

  useEffect(() => {
    if (currentProviders.length > 0) {
      setSelectedProviders(
        currentProviders.map((p) => ({
          id: p.providerId,
          label: p.providerLabel,
          specialty: '',
          practice: '',
        }))
      );
    } else {
      setSelectedProviders([]);
    }
  }, [currentProviders]);

  const updateField = useCallback(
    (
      value: string,
      providers?: { providerId: string; providerLabel: string }[],
      who?: string,
      what?: string
    ) => {
      const updatedField = {
        value,
        metadata: {
          providers: providers || currentProviders,
          whoProvidesCoverage: who || currentWho,
          whatAppointments: what || currentWhat,
        },
        id: field?.id,
        rowId: field?.rowId || rowId || null,
        fieldId: fieldId,
        startDate: field?.startDate || null,
        endDate: field?.endDate || null,
      };

      onChange(updatedField);
    },
    [
      field,
      onChange,
      currentProviders,
      currentWho,
      currentWhat,
      fieldId,
      rowId,
    ]
  );

  const handleProvidersChange = useCallback(
    (providers: ProviderSearchResult[]) => {
      setSelectedProviders(providers);

      const fieldProviders = providers.map((p) => ({
        providerId: p.id,
        providerLabel: p.label,
      }));

      updateField(
        currentValue,
        fieldProviders,
        currentWho,
        currentWhat
      );
    },
    [currentValue, currentWho, currentWhat, updateField]
  );

  const handleRadioChange = useCallback(
    (value: string) => {
      if (value === 'None') {
        const resetField = {
          value: 'None',
          metadata: {
            providers: [],
            whoProvidesCoverage: '',
            whatAppointments: '',
          },
          id: field?.id,
          rowId: field?.rowId || rowId || null,
          fieldId: fieldId,
          startDate: field?.startDate || null,
          endDate: field?.endDate || null,
        };

        setSelectedProviders([]);
        onChange(resetField);
      } else if (value === 'Yes') {
        updateField('Yes', currentProviders, currentWho, currentWhat);
      }
    },
    [
      field,
      rowId,
      fieldId,
      currentProviders,
      currentWho,
      currentWhat,
      updateField,
      onChange,
    ]
  );

  const handleWhoChange = useCallback(
    (who: string) => {
      updateField(currentValue, currentProviders, who, currentWhat);
    },
    [currentValue, currentProviders, currentWhat, updateField]
  );

  const handleWhatChange = useCallback(
    (what: string) => {
      updateField(currentValue, currentProviders, currentWho, what);
    },
    [currentValue, currentProviders, currentWho, updateField]
  );

  return (
    <Box>
      <VStack align="start" spacing={4} width="100%">
        <RadioGroup
          value={currentValue || 'None'}
          onChange={handleRadioChange}
          mb="0.5rem"
        >
          <VStack spacing={4} align="start" width="100%">
            <Box
              p="1rem 1.25rem"
              borderRadius="md"
              border="1px solid"
              borderColor="gray.200"
              bg="white"
              cursor="pointer"
              width="100%"
              _hover={{
                bg: 'gray.50',
                borderColor: 'gray.300',
                transform: 'translateY(-1px)',
                boxShadow: 'sm',
              }}
              transition="all 0.2s"
              onClick={() => handleRadioChange('None')}
            >
              <Radio value="None" size="md" pointerEvents="none">
                <Box>
                  <Text fontWeight="600" color="gray.700">
                    None
                  </Text>
                  <Text fontSize="0.75rem" color="gray.500">
                    No cross coverage specified
                  </Text>
                </Box>
              </Radio>
            </Box>

            <Box
              p="1rem 1.25rem"
              borderRadius="md"
              border="1px solid"
              borderColor="gray.200"
              bg="white"
              cursor="pointer"
              width="100%"
              _hover={{
                bg: 'gray.50',
                borderColor: 'gray.300',
                transform: 'translateY(-1px)',
                boxShadow: 'sm',
              }}
              transition="all 0.2s"
              onClick={() => handleRadioChange('Yes')}
            >
              <Radio value="Yes" size="md" pointerEvents="none">
                <Box>
                  <Text fontWeight="600" color="gray.700">
                    Yes
                  </Text>
                  <Text fontSize="0.75rem" color="gray.500">
                    Provide who covers and what appointment types
                  </Text>
                </Box>
              </Radio>
            </Box>
          </VStack>
        </RadioGroup>

        {currentValue === 'Yes' && (
          <>
            <Box width="100%">
              <Text
                fontSize="0.875rem"
                fontWeight="500"
                color="gray.700"
                mb={2}
              >
                Who Provides Coverage
              </Text>
              <Input
                value={currentWho}
                onChange={(e) => handleWhoChange(e.target.value)}
                placeholder="Add instructions about coverage (e.g., rotation notes, days covered)"
                size="md"
                borderRadius="md"
                borderColor="gray.300"
                _hover={{ borderColor: 'gray.400' }}
                _focus={{
                  borderColor: 'blue.500',
                  boxShadow:
                    '0 0 0 1px var(--chakra-colors-blue-500)',
                }}
              />
            </Box>

            <Box width="100%">
              <Text
                fontSize="0.875rem"
                fontWeight="500"
                color="gray.700"
                mb={2}
              >
                What Appointments
              </Text>
              <Input
                value={currentWhat || ''}
                onChange={(e) => handleWhatChange(e.target.value)}
                placeholder="Enter what appointment types are covered (e.g., routine visits, follow-ups)"
                size="md"
                borderRadius="md"
                borderColor="gray.300"
                _hover={{ borderColor: 'gray.400' }}
                _focus={{
                  borderColor: 'blue.500',
                  boxShadow:
                    '0 0 0 1px var(--chakra-colors-blue-500)',
                }}
              />
            </Box>

            <Box
              width="100%"
              p="1rem"
              bg="blue.50"
              borderRadius="md"
              border="1px solid"
              borderColor="blue.200"
            >
              <Text fontSize="0.75rem" color="blue.700" mb={2}>
                Optionally link specific providers in addition to the
                free text.
              </Text>
            </Box>

            <Box width="100%">
              <Text
                fontSize="0.875rem"
                fontWeight="500"
                color="gray.700"
                mb={2}
              >
                Search and Link Providers
              </Text>
              <ProviderSearchSelect
                selectedProviders={selectedProviders}
                onProvidersChange={handleProvidersChange}
                placeholder="Search for providers to link"
              />
            </Box>
          </>
        )}
      </VStack>
    </Box>
  );
};
